/**
 * particles/ambient.ts — Ambient Particle System
 *
 * Rooms breathe. Dust floats in lamplight. Embers drift upward from a
 * fireplace and gutter out before they reach the ceiling. The Aeterna pulses
 * with cool motes that orbit and fade. A window in afternoon light casts
 * slow-shifting shafts across the floor.
 *
 * None of this is strictly necessary for the game to function. All of it is
 * necessary for the room to feel like a place someone actually lives in.
 *
 * Architecture: typed arrays for the particle pool — no per-frame allocation,
 * no GC pressure. All state lives in flat buffers; indices are compacted by
 * swap-and-pop when particles expire. The emitter logic runs inside update()
 * and consults the room's furniture list each tick, so new lamps and
 * fireplaces start emitting immediately without any registration step.
 *
 * Rendering batches by composite mode: all additive-blend particles draw
 * together so we touch globalCompositeOperation as few times as possible.
 * Light rays are drawn as filled polygons entirely outside the particle loop.
 *
 * "Does this feel like home?" — yes. Because the dust is there.
 */

import { worldToScreen, applyCamera, type Camera } from "../iso/projection.js";
import type { GameState, FurniturePlacement } from "../engine/state.js";
import { getMobileState } from "../engine/mobile.js";

// ─── Particle Types ───────────────────────────────────────────────────────────

/** Dust mote floating in lamplight or window light */
const TYPE_DUST  = 0;
/** Fireplace ember drifting upward */
const TYPE_EMBER = 1;
/** Aeterna orbital glow particle */
const TYPE_GLOW  = 2;
/** Window light ray — rendered as polygon, not a point particle */
const TYPE_RAY   = 3;
/** Combat impact spark — fast radial burst, heavy gravity, short life */
const TYPE_IMPACT = 4;

// ─── Emitter Accumulator Keys ─────────────────────────────────────────────────

/**
 * Per-furniture emission accumulators.
 * We accumulate fractional particles per-second using the furniture id as key
 * so that each emitter fires independently and correctly regardless of dt
 * variability. The map is cleared when the room changes.
 */
type EmitAccumMap = Map<string, number>;

// ─── Light Ray State ─────────────────────────────────────────────────────────

/**
 * One window light shaft. Maintained in a separate array so the particle pool
 * isn't polluted with polygon state. The angle drifts over time.
 */
interface LightRay {
  /** Screen X of the ray origin (window center, pre-computed) */
  originX: number;
  /** Screen Y of the ray origin */
  originY: number;
  /** Current angle offset in radians — drifts slowly with time */
  angle: number;
  /** Angular drift rate, rad/s (tiny — 5° over 60s = ~0.00145 rad/s) */
  driftRate: number;
  /** Shaft half-width at the source (pixels, narrow end) */
  nearHalf: number;
  /** Shaft half-width at the far end (pixels, wide end) */
  farHalf: number;
  /** How far the shaft reaches from the window (pixels) */
  reach: number;
  /** Base alpha before time-of-day attenuation */
  baseAlpha: number;
}

// ─── ParticleSystem ───────────────────────────────────────────────────────────

export class ParticleSystem {
  // ── Pool capacity ──────────────────────────────────────────────────────────
  private readonly MAX = 250;

  // ── Particle attribute buffers ────────────────────────────────────────────
  // Every attribute is a separate typed array so we can iterate a single
  // attribute cache-linearly without touching other attributes.
  private readonly x:       Float32Array;  // screen X
  private readonly y:       Float32Array;  // screen Y
  private readonly vx:      Float32Array;  // velocity X, px/s
  private readonly vy:      Float32Array;  // velocity Y, px/s
  private readonly life:    Float32Array;  // remaining life, seconds
  private readonly maxLife: Float32Array;  // total life, seconds (for fade curve)
  private readonly size:    Float32Array;  // radius or half-side, px
  private readonly r:       Float32Array;  // color red   0–255
  private readonly g:       Float32Array;  // color green 0–255
  private readonly b:       Float32Array;  // color blue  0–255
  private readonly alpha:   Float32Array;  // base alpha  0–1
  private readonly type:    Uint8Array;    // TYPE_DUST / EMBER / GLOW

  // ── Glow orbital data ─────────────────────────────────────────────────────
  // Only used when type === TYPE_GLOW. Stored separately to keep main
  // buffers tight for the common (non-glow) particle iteration.
  private readonly glowAngle:        Float32Array;  // current orbital angle, rad
  private readonly glowAngularVel:   Float32Array;  // rad/s
  private readonly glowRadius:       Float32Array;  // orbital radius, px
  private readonly glowCenterX:      Float32Array;  // center screen X
  private readonly glowCenterY:      Float32Array;  // center screen Y

  // ── Live count ────────────────────────────────────────────────────────────
  private count = 0;

  // ── Emitter state ─────────────────────────────────────────────────────────
  private readonly emitAccum: EmitAccumMap = new Map();
  private lastRoomId: string | null = null;

  // ── Light ray state ───────────────────────────────────────────────────────
  private lightRays: LightRay[] = [];
  private lastRayRoomId: string | null = null;

  // ── Global time accumulator (for brownian noise seed variation) ───────────
  private totalTime = 0;

  // ─── Constructor ────────────────────────────────────────────────────────────

  constructor() {
    const N = this.MAX;
    this.x          = new Float32Array(N);
    this.y          = new Float32Array(N);
    this.vx         = new Float32Array(N);
    this.vy         = new Float32Array(N);
    this.life       = new Float32Array(N);
    this.maxLife    = new Float32Array(N);
    this.size       = new Float32Array(N);
    this.r          = new Float32Array(N);
    this.g          = new Float32Array(N);
    this.b          = new Float32Array(N);
    this.alpha      = new Float32Array(N);
    this.type       = new Uint8Array(N);

    this.glowAngle      = new Float32Array(N);
    this.glowAngularVel = new Float32Array(N);
    this.glowRadius     = new Float32Array(N);
    this.glowCenterX    = new Float32Array(N);
    this.glowCenterY    = new Float32Array(N);
  }

  // ─── Public API ─────────────────────────────────────────────────────────────

  /**
   * Advance all particles by one tick and fire emitters.
   *
   * @param dt      Seconds since last frame
   * @param state   Full game state (room, time, etc.)
   * @param cam     Current camera
   * @param quality 0.5–1.0 quality scalar; throttles emission at low values
   */
  update(dt: number, state: GameState, cam: Camera, quality: number): void {
    this.totalTime += dt;

    const room = state.currentRoom;
    if (!room) return;

    // Respect reduced motion preference — skip all particle emission and physics.
    // Light rays still exist (they're very slow-moving) but particles don't spawn.
    const mobile = getMobileState();
    if (mobile?.prefersReducedMotion) {
      // Still kill expired particles so the pool drains, but don't spawn new ones
      this._killExpired(dt);
      return;
    }

    // Clear per-room state when we enter a new room
    if (room.id !== this.lastRoomId) {
      this.count = 0;
      this.emitAccum.clear();
      this.lastRoomId = room.id;
    }

    // ── 1. Kill expired particles (swap-and-pop) ────────────────────────────
    this._killExpired(dt);

    // ── 2. Physics update ──────────────────────────────────────────────────
    this._applyPhysics(dt);

    // ── 3. Fire emitters (quality-gated) ───────────────────────────────────
    if (quality >= 0.55) {
      const emitScale = quality >= 0.7 ? 1.0 : 0.5;
      this._runEmitters(dt, state, cam, emitScale);
    }

    // ── 4. Rebuild light rays if room changed ──────────────────────────────
    if (room.id !== this.lastRayRoomId) {
      this._buildLightRays(room, cam);
      this.lastRayRoomId = room.id;
    }

    // ── 5. Drift light ray angles ──────────────────────────────────────────
    for (const ray of this.lightRays) {
      ray.angle += ray.driftRate * dt;
    }
  }

  /**
   * Emit a burst of combat impact sparks at a world position.
   * Called when a player attack connects with a creature.
   *
   * @param screenX  Screen X of the impact point
   * @param screenY  Screen Y of the impact point
   * @param isCrit   True for critical hits — brighter, more particles
   */
  emitImpact(screenX: number, screenY: number, isCrit: boolean): void {
    const count = isCrit ? 12 : 8;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
      const speed = 40 + Math.random() * 60 + (isCrit ? 30 : 0);
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed - 20; // bias upward
      const life = 0.25 + Math.random() * 0.25;
      const sz = 1.0 + Math.random() * (isCrit ? 1.5 : 0.8);

      // Colors: gold/orange sparks normally, white/bright for crits
      let rr: number, gg: number, bb: number;
      if (isCrit) {
        rr = 255; gg = 220 + Math.random() * 35; bb = 100 + Math.random() * 80;
      } else {
        rr = 255; gg = 160 + Math.random() * 60; bb = 40 + Math.random() * 40;
      }

      this._spawn(TYPE_IMPACT, screenX, screenY, vx, vy, life, sz, rr, gg, bb, 0.9);
    }
  }

  /**
   * Draw all live particles and light rays onto the canvas.
   *
   * Render order:
   *   A. Light rays (polygons, very low alpha)
   *   B. Additive-blend particles (dust, glow) — drawn together to minimise
   *      composite mode switches
   *   C. Normal-blend particles (embers)
   *
   * @param ctx     2D canvas context
   * @param cam     Current camera (used for ray re-projection; not for particles
   *                because their positions are already in screen space)
   * @param quality 0.5–1.0; rays are skipped below 0.8
   */
  render(ctx: CanvasRenderingContext2D, cam: Camera, quality: number): void {
    if (this.count === 0 && this.lightRays.length === 0) return;

    ctx.save();

    // ── A. Light rays (re-projected each frame to follow camera) ────────────
    if (quality >= 0.8 && this.lightRays.length > 0) {
      this._renderLightRays(ctx, cam);
    }

    // ── B. Additive particles (dust + glow + impact sparks) ──────────────────
    ctx.globalCompositeOperation = "lighter";
    this._renderByType(ctx, TYPE_DUST);
    this._renderByType(ctx, TYPE_GLOW);
    this._renderByType(ctx, TYPE_IMPACT);

    // ── C. Normal particles (embers) ────────────────────────────────────────
    ctx.globalCompositeOperation = "source-over";
    this._renderByType(ctx, TYPE_EMBER);

    ctx.restore();
  }

  // ─── Private — Kill & Physics ────────────────────────────────────────────────

  /**
   * Decrement life and compact dead particles via swap-and-pop.
   * Iterating backward avoids invalidating the index after a swap.
   */
  private _killExpired(dt: number): void {
    for (let i = this.count - 1; i >= 0; i--) {
      this.life[i] -= dt;
      if (this.life[i] <= 0) {
        this._swapWithLast(i);
        this.count--;
      }
    }
  }

  /**
   * Apply type-specific physics to every live particle.
   */
  private _applyPhysics(dt: number): void {
    for (let i = 0; i < this.count; i++) {
      const t = this.type[i];

      if (t === TYPE_DUST) {
        // Brownian motion: small random perturbation each frame
        // We use a deterministic hash of (index, time) so we don't need
        // a per-particle RNG seed while still varying between frames.
        const noiseX = (pseudoRand(i * 1973 + (this.totalTime * 97) | 0) - 0.5) * 0.6;
        const noiseY = (pseudoRand(i * 3571 + (this.totalTime * 83) | 0) - 0.5) * 0.6;
        this.vx[i] += noiseX;
        this.vy[i] += noiseY;
        // Soft-clamp velocity so motes don't accumulate speed
        this.vx[i] *= 0.92;
        this.vy[i] *= 0.92;
        // Slight constant upward drift — hot air near a lamp rises
        this.vy[i] -= 0.08;

      } else if (t === TYPE_EMBER) {
        // Gravity pulls embers back down as they cool
        this.vy[i] += 5.0 * dt;
        // Horizontal damping simulates air resistance
        this.vx[i] *= 1.0 - 1.8 * dt;

      } else if (t === TYPE_IMPACT) {
        // Impact sparks: heavy gravity pulls them down fast, strong air resistance
        this.vy[i] += 120.0 * dt;  // much heavier gravity than embers
        this.vx[i] *= Math.exp(-4.0 * dt);  // frame-rate independent drag
        this.vy[i] *= Math.exp(-2.0 * dt);

      } else if (t === TYPE_GLOW) {
        // Orbital motion: advance angle and recompute position from center
        this.glowAngle[i] += this.glowAngularVel[i] * dt;
        const a = this.glowAngle[i];
        const rad = this.glowRadius[i];
        this.x[i] = this.glowCenterX[i] + Math.cos(a) * rad;
        this.y[i] = this.glowCenterY[i] + Math.sin(a) * rad;
        // No velocity integration for glow — position is derived from angle
        continue;
      }

      // Integrate velocity for dust and embers
      this.x[i] += this.vx[i] * dt;
      this.y[i] += this.vy[i] * dt;
    }
  }

  // ─── Private — Emitters ──────────────────────────────────────────────────────

  /**
   * Walk the room furniture list and spawn particles from active emitters.
   * Also spawns dust near windowed wall segments.
   */
  private _runEmitters(
    dt: number,
    state: GameState,
    cam: Camera,
    emitScale: number
  ): void {
    const room = state.currentRoom;
    if (!room) return;

    // Furniture emitters
    for (const f of room.furniture) {
      const ft = f.type.toLowerCase();

      if (ft === "floor_lamp" && f.state["on"] !== false) {
        this._emitDustLamp(dt, f, cam, emitScale);
      } else if (ft === "fireplace" || ft.includes("fire")) {
        this._emitEmbers(dt, f, cam, emitScale);
      } else if (ft === "aeterna") {
        this._emitGlow(dt, f, cam, emitScale);
      }
    }

    // Window dust — lazy check: any wall with hasWindow near the room edges
    this._emitWindowDust(dt, room, cam, emitScale);
  }

  /**
   * Dust motes floating in the warm cone of a floor lamp.
   * Emit 1–2 per second; scatter within 80px of the lamp head position.
   */
  private _emitDustLamp(
    dt: number,
    f: FurniturePlacement,
    cam: Camera,
    emitScale: number
  ): void {
    // The lamp head sits roughly 1.6 world units up from the base
    const screen = applyCamera(
      worldToScreen(f.tileX + 0.5, f.tileY + 0.5, f.elevation + 1.6, true),
      cam
    );

    const ratePerSec = 1.5 * emitScale;
    const count = this._accumulate(f.id + "_dust", dt * ratePerSec);

    for (let n = 0; n < count; n++) {
      const scatter = 80;
      const sx = screen.x + (rand() - 0.5) * scatter;
      const sy = screen.y + (rand() - 0.5) * scatter * 0.5;

      const vx = (rand() - 0.5) * 0.3;
      const vy = -0.1 - rand() * 0.1;            // gentle upward drift
      const life = 4 + rand() * 4;               // 4–8 s
      const sz   = 1 + rand();                   // 1–2 px
      const al   = 0.10 + rand() * 0.15;         // 0.10–0.25

      this._spawn(TYPE_DUST, sx, sy, vx, vy, life, sz, 255, 240, 200, al);
    }
  }

  /**
   * Dust motes drifting in through windows.
   * Emits along the screen positions of windowed wall segments.
   */
  private _emitWindowDust(
    dt: number,
    room: import("../engine/state.js").Room,
    cam: Camera,
    emitScale: number
  ): void {
    for (const wall of room.walls) {
      if (!wall.hasWindow) continue;

      const key = `window_${wall.x}_${wall.y}_${wall.side}`;
      const ratePerSec = 0.8 * emitScale;
      const count = this._accumulate(key, dt * ratePerSec);
      if (count === 0) continue;

      // Window center: halfway up the wall face
      const screen = applyCamera(
        worldToScreen(wall.x + 0.5, wall.y + 0.5, 1.5, true),
        cam
      );

      for (let n = 0; n < count; n++) {
        const sx = screen.x + (rand() - 0.5) * 40;
        const sy = screen.y + (rand() - 0.5) * 24;
        // Window dust drifts inward and very slightly upward
        const vx = (rand() - 0.5) * 0.4;
        const vy = -0.05 - rand() * 0.1;
        const life = 5 + rand() * 3;
        const sz   = 1 + rand() * 0.5;
        const al   = 0.08 + rand() * 0.12;
        this._spawn(TYPE_DUST, sx, sy, vx, vy, life, sz, 255, 248, 220, al);
      }
    }
  }

  /**
   * Fireplace embers: launch upward with jitter, subject to gravity.
   * Emit 3–5 per second from just above the firebox.
   */
  private _emitEmbers(
    dt: number,
    f: FurniturePlacement,
    cam: Camera,
    emitScale: number
  ): void {
    // Firebox opening sits ~0.3 world units above the base
    const screen = applyCamera(
      worldToScreen(f.tileX + 0.7, f.tileY + 0.5, f.elevation + 0.3, true),
      cam
    );

    const ratePerSec = (3 + rand() * 2) * emitScale;
    const count = this._accumulate(f.id + "_emb", dt * ratePerSec);

    for (let n = 0; n < count; n++) {
      const sx  = screen.x + (rand() - 0.5) * 8;
      const sy  = screen.y + (rand() - 0.5) * 4;
      const vx  = (rand() - 0.5) * 10;           // horizontal jitter ±10 px/s
      const vy  = -(15 + rand() * 25);            // 15–40 px/s upward
      const life = 1 + rand() * 2;               // 1–3 s
      const sz   = 1 + rand() * 2;               // 1–3 px
      const al   = 0.75 + rand() * 0.2;          // 0.75–0.95

      // Orange-red with randomised warmth channel
      const rr = 255;
      const gg = 120 + (rand() * 80) | 0;
      const bb = 20  + (rand() * 60) | 0;

      this._spawn(TYPE_EMBER, sx, sy, vx, vy, life, sz, rr, gg, bb, al);
    }
  }

  /**
   * Aeterna orbital glow: particles circle the device in tight orbits.
   * Emit 1–2 per second with randomised radius, angle, and angular velocity.
   */
  private _emitGlow(
    dt: number,
    f: FurniturePlacement,
    cam: Camera,
    emitScale: number
  ): void {
    const screen = applyCamera(
      worldToScreen(f.tileX + 0.5, f.tileY + 0.5, f.elevation + 0.5, true),
      cam
    );

    const ratePerSec = 1.5 * emitScale;
    const count = this._accumulate(f.id + "_glow", dt * ratePerSec);

    for (let n = 0; n < count; n++) {
      const life   = 2 + rand() * 2;              // 2–4 s
      const radius = 5 + rand() * 10;             // 5–15 px orbital radius
      const startAngle = rand() * Math.PI * 2;
      // Some particles orbit CW, some CCW — mix of ±0.4–1.2 rad/s
      const angVel = (rand() < 0.5 ? 1 : -1) * (0.4 + rand() * 0.8);
      const al = 0.30 + rand() * 0.30;            // 0.30–0.60
      const sz = 1;

      // Blue-white — cool and otherworldly
      const rr = 140; const gg = 180; const bb = 255;

      const i = this.count;
      this._spawn(TYPE_GLOW, screen.x, screen.y, 0, 0, life, sz, rr, gg, bb, al);
      if (i < this.count) {
        // Initialise orbital data for this slot
        this.glowAngle[i]      = startAngle;
        this.glowAngularVel[i] = angVel;
        this.glowRadius[i]     = radius;
        this.glowCenterX[i]    = screen.x;
        this.glowCenterY[i]    = screen.y;
        // Set initial position on the orbit
        this.x[i] = screen.x + Math.cos(startAngle) * radius;
        this.y[i] = screen.y + Math.sin(startAngle) * radius;
      }
    }
  }

  // ─── Private — Spawn ─────────────────────────────────────────────────────────

  /**
   * Write one particle into the next free pool slot.
   * No-op if the pool is full.
   */
  private _spawn(
    particleType: number,
    sx: number, sy: number,
    pvx: number, pvy: number,
    life: number,
    size: number,
    pr: number, pg: number, pb: number,
    alpha: number
  ): void {
    if (this.count >= this.MAX) return;
    const i = this.count++;
    this.x[i]       = sx;
    this.y[i]       = sy;
    this.vx[i]      = pvx;
    this.vy[i]      = pvy;
    this.life[i]    = life;
    this.maxLife[i] = life;
    this.size[i]    = size;
    this.r[i]       = pr;
    this.g[i]       = pg;
    this.b[i]       = pb;
    this.alpha[i]   = alpha;
    this.type[i]    = particleType;
  }

  /**
   * Swap particle at index i with the last live particle, then let the caller
   * decrement count. This preserves pool compactness without shifting arrays.
   */
  private _swapWithLast(i: number): void {
    const last = this.count - 1;
    if (i === last) return;
    this.x[i]          = this.x[last];
    this.y[i]          = this.y[last];
    this.vx[i]         = this.vx[last];
    this.vy[i]         = this.vy[last];
    this.life[i]       = this.life[last];
    this.maxLife[i]    = this.maxLife[last];
    this.size[i]       = this.size[last];
    this.r[i]          = this.r[last];
    this.g[i]          = this.g[last];
    this.b[i]          = this.b[last];
    this.alpha[i]      = this.alpha[last];
    this.type[i]       = this.type[last];
    // Glow orbital data
    this.glowAngle[i]      = this.glowAngle[last];
    this.glowAngularVel[i] = this.glowAngularVel[last];
    this.glowRadius[i]     = this.glowRadius[last];
    this.glowCenterX[i]   = this.glowCenterX[last];
    this.glowCenterY[i]   = this.glowCenterY[last];
  }

  // ─── Private — Accumulator ───────────────────────────────────────────────────

  /**
   * Accumulate fractional particle counts and return the integer portion.
   *
   * Emitters want to fire at e.g. 1.5 particles/second. Each frame we add
   * dt * ratePerSec to the accumulator; when it crosses 1.0 we floor it,
   * subtract the integer part, and return how many to spawn this frame.
   *
   * This gives exactly the right average rate regardless of frame timing.
   */
  private _accumulate(key: string, amount: number): number {
    const prev  = this.emitAccum.get(key) ?? 0;
    const total = prev + amount;
    const emit  = Math.floor(total);
    this.emitAccum.set(key, total - emit);
    return emit;
  }

  // ─── Private — Light Rays ────────────────────────────────────────────────────

  /**
   * Build (or rebuild) the light ray descriptors for all windowed walls.
   * Called once per room transition. Each window spawns 2–3 shafts with
   * slightly different angles and alphas.
   */
  private _buildLightRays(
    room: import("../engine/state.js").Room,
    _cam: Camera
  ): void {
    this.lightRays = [];

    for (const wall of room.walls) {
      if (!wall.hasWindow) continue;

      // Store world-space origin so rays follow camera pans/zooms
      const worldOrigin = worldToScreen(wall.x + 0.5, wall.y + 0.5, 2.0, true);

      const shaftCount = 2 + Math.floor(rand() * 2); // 2 or 3
      for (let s = 0; s < shaftCount; s++) {
        const baseAngle = wall.side === "N"
          ? Math.PI / 2 + 0.3 + (s - 1) * 0.15
          : Math.PI / 4 + 0.2 + (s - 1) * 0.12;

        this.lightRays.push({
          originX:   worldOrigin.x + (rand() - 0.5) * 20,
          originY:   worldOrigin.y,
          angle:     baseAngle,
          driftRate: (rand() - 0.5) * 0.0029,
          nearHalf:  4 + rand() * 4,
          farHalf:   20 + rand() * 20,
          reach:     120 + rand() * 80,
          baseAlpha: 0.03 + rand() * 0.02,
        });
      }
    }
  }

  /**
   * Draw all light rays as filled trapezoids. Only called when quality >= 0.8
   * and daytime hours (game hour 6–18) — checked in render().
   */
  private _renderLightRays(ctx: CanvasRenderingContext2D, cam: Camera): void {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";

    for (const ray of this.lightRays) {
      // Project from world-space to screen-space each frame
      const ox = ray.originX * cam.zoom + cam.x;
      const oy = ray.originY * cam.zoom + cam.y;
      const scaledReach = ray.reach * cam.zoom;
      const scaledNear  = ray.nearHalf * cam.zoom;
      const scaledFar   = ray.farHalf * cam.zoom;

      const dx = Math.cos(ray.angle);
      const dy = Math.sin(ray.angle);
      const px = -dy;
      const py =  dx;

      const fx = ox + dx * scaledReach;
      const fy = oy + dy * scaledReach;

      ctx.fillStyle = `rgba(255,240,200,${ray.baseAlpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.moveTo(ox + px * scaledNear, oy + py * scaledNear);
      ctx.lineTo(ox - px * scaledNear, oy - py * scaledNear);
      ctx.lineTo(fx - px * scaledFar,  fy - py * scaledFar);
      ctx.lineTo(fx + px * scaledFar,  fy + py * scaledFar);
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  }

  // ─── Private — Render Helpers ────────────────────────────────────────────────

  /**
   * Draw all live particles of the given type using the current composite mode.
   * The caller is responsible for setting globalCompositeOperation before calling.
   */
  private _renderByType(ctx: CanvasRenderingContext2D, particleType: number): void {
    for (let i = 0; i < this.count; i++) {
      if (this.type[i] !== particleType) continue;

      const life    = this.life[i];
      const maxLife = this.maxLife[i];
      const t       = life / maxLife;        // 1 = just born, 0 = about to die
      const baseAlpha = this.alpha[i];

      let screenAlpha: number;

      if (particleType === TYPE_DUST) {
        // Fade in over first 20%, hold, fade out over last 30%
        const fadeIn  = Math.min(1, (1 - t) / 0.20);  // ramps 0→1 as t falls from 1→0.8
        const fadeOut = Math.min(1, t / 0.30);          // ramps 0→1 as t rises from 0→0.30
        screenAlpha   = baseAlpha * Math.min(fadeIn, fadeOut);

      } else if (particleType === TYPE_EMBER) {
        // Mostly bright, with a brief flare in the last 20%
        const normalFade = Math.min(1, t / 0.20);      // full alpha until t < 0.20
        const flare      = t < 0.20 ? 1 + (0.20 - t) / 0.20 * 0.4 : 1.0;
        screenAlpha      = baseAlpha * normalFade * flare;

      } else if (particleType === TYPE_GLOW) {
        // Soft pulse: sin wave on life phase, plus fade at ends
        const fadeIn  = Math.min(1, (1 - t) / 0.15);
        const fadeOut = Math.min(1, t / 0.25);
        const pulse   = 0.85 + 0.15 * Math.sin(life * 4);
        screenAlpha   = baseAlpha * Math.min(fadeIn, fadeOut) * pulse;

      } else if (particleType === TYPE_IMPACT) {
        // Impact sparks: bright flash then rapid fade
        screenAlpha = baseAlpha * t * t; // quadratic fade — bright at birth, gone fast

      } else {
        screenAlpha = baseAlpha * t;
      }

      if (screenAlpha <= 0.001) continue;

      const px  = this.x[i];
      const py  = this.y[i];
      const sz  = this.size[i];
      const rr  = this.r[i] | 0;
      const gg  = this.g[i] | 0;
      const bb  = this.b[i] | 0;

      ctx.fillStyle = `rgba(${rr},${gg},${bb},${screenAlpha.toFixed(3)})`;

      if (sz <= 1.5) {
        // Sub-2px particles: simple fill rect — fastest path
        ctx.fillRect(px - sz * 0.5, py - sz * 0.5, sz, sz);
      } else {
        // Larger particles: soft circle
        ctx.beginPath();
        ctx.arc(px, py, sz, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
}

// ─── Utility ─────────────────────────────────────────────────────────────────

/**
 * Cheap seedable pseudo-random number in [0, 1).
 * Used for deterministic per-particle noise — avoids needing a per-particle
 * RNG state while still varying coherently between frames.
 *
 * Algorithm: Wang hash, fast and good enough for visual noise.
 */
function pseudoRand(seed: number): number {
  let s = (seed ^ 61) ^ (seed >>> 16);
  s = (s + (s << 3)) | 0;
  s ^= s >>> 4;
  s = Math.imul(s, 0x27d4eb2d);
  s ^= s >>> 15;
  return (s >>> 0) / 0xffffffff;
}

/**
 * Plain Math.random alias — used for spawn-time randomisation where
 * determinism is not needed (each spawn is a fresh event).
 */
function rand(): number {
  return Math.random();
}
